import {
  ActionIcon,
  Badge,
  Button,
  Card,
  Checkbox,
  Group,
  Modal,
  PasswordInput,
  Stack,
  Table,
  Text,
  TextInput,
  Textarea,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';

import type { Entry, EntrySet } from '../api/gen';
import { Refusal } from './Refusal';

/**
 * One scope's configuration: the project's base or one environment's
 * overrides. The two have the same shape and the same verbs, so this is one
 * table over whichever pair of calls the page hands it.
 *
 * A secret is write-only. The server never reads its value back — the entry
 * says only whether one is stored — so the table masks it and editing one
 * starts from an empty field: to change a secret is to replace it.
 */
export function ConfigEditor({
  entries,
  loading,
  error,
  onSet,
  onDelete,
  empty,
}: {
  entries: Entry[] | undefined;
  loading: boolean;
  error: unknown;
  onSet: (key: string, body: EntrySet) => Promise<unknown>;
  onDelete: (key: string) => Promise<unknown>;
  empty: string;
}) {
  const [opened, { open, close }] = useDisclosure();
  const [key, setKey] = useState('');
  const [value, setValue] = useState('');
  const [secret, setSecret] = useState(false);
  const [editing, setEditing] = useState(false);

  const set = useMutation({
    mutationFn: () => onSet(key, { value, isSecret: secret }),
    onSuccess: () => close(),
  });
  const del = useMutation({ mutationFn: (k: string) => onDelete(k) });

  const start = (e?: Entry) => {
    set.reset();
    setEditing(!!e);
    setKey(e?.key ?? '');
    setSecret(e?.isSecret ?? false);
    setValue(e && !e.isSecret ? (e.value ?? '') : '');
    open();
  };

  return (
    <Stack>
      <Group justify="flex-end">
        <Button variant="light" onClick={() => start()}>
          Add value
        </Button>
      </Group>
      <Refusal error={error} />
      <Refusal error={del.error} />
      <Card withBorder p={0}>
        <Table striped highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Key</Table.Th>
              <Table.Th>Value</Table.Th>
              <Table.Th>Updated</Table.Th>
              <Table.Th />
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {loading && (
              <Table.Tr>
                <Table.Td colSpan={4}>Loading…</Table.Td>
              </Table.Tr>
            )}
            {entries?.map((e) => (
              <Table.Tr key={e.key}>
                <Table.Td>
                  <Group gap="xs">
                    <code>{e.key}</code>
                    {e.isSecret && (
                      <Badge size="xs" variant="light" color="grape">
                        secret
                      </Badge>
                    )}
                  </Group>
                </Table.Td>
                <Table.Td>
                  {e.isSecret ? (
                    <Text size="sm" c="dimmed" ff="monospace">
                      {e.hasValue ? '••••••••' : '(not set)'}
                    </Text>
                  ) : (
                    <Text size="sm" ff="monospace" style={{ wordBreak: 'break-all' }}>
                      {e.value}
                    </Text>
                  )}
                </Table.Td>
                <Table.Td>
                  <Text size="sm">{new Date(e.updatedAt).toLocaleString()}</Text>
                </Table.Td>
                <Table.Td>
                  <Group gap={4} justify="flex-end" wrap="nowrap">
                    <Button size="compact-xs" variant="subtle" onClick={() => start(e)}>
                      {e.isSecret ? 'Replace' : 'Edit'}
                    </Button>
                    <ActionIcon
                      variant="subtle"
                      color="red"
                      aria-label={`Remove ${e.key}`}
                      loading={del.isPending && del.variables === e.key}
                      onClick={() => {
                        if (confirm(`Remove ${e.key}?`)) del.mutate(e.key);
                      }}
                    >
                      ×
                    </ActionIcon>
                  </Group>
                </Table.Td>
              </Table.Tr>
            ))}
            {entries?.length === 0 && (
              <Table.Tr>
                <Table.Td colSpan={4}>
                  <Text c="dimmed">{empty}</Text>
                </Table.Td>
              </Table.Tr>
            )}
          </Table.Tbody>
        </Table>
      </Card>

      <Modal opened={opened} onClose={close} title={editing ? `Set ${key}` : 'Add value'}>
        <Stack>
          <TextInput
            label="Key"
            value={key}
            disabled={editing}
            onChange={(e) => setKey(e.currentTarget.value)}
          />
          <Checkbox
            label="Secret — encrypted at rest and never shown again"
            checked={secret}
            onChange={(e) => setSecret(e.currentTarget.checked)}
          />
          {secret ? (
            <PasswordInput
              label="Value"
              description={editing ? 'The stored value cannot be read back; this replaces it.' : undefined}
              value={value}
              onChange={(e) => setValue(e.currentTarget.value)}
            />
          ) : (
            <Textarea label="Value" autosize minRows={2} value={value} onChange={(e) => setValue(e.currentTarget.value)} />
          )}
          <Refusal error={set.error} />
          <Button disabled={!key} loading={set.isPending} onClick={() => set.mutate()}>
            Save
          </Button>
        </Stack>
      </Modal>
    </Stack>
  );
}
